import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input, TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { FormField } from "@tql/ui-components-react/tql/form/beta";
import {
	Checkbox,
	NumericTextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import { useAtomValue } from "jotai";
import { useState } from "react";

import { useLoadValues } from "~/features/ftl/ftl-quotes/hooks/use-load-values.tsx";
import { useTrailerSizes } from "~/features/ftl/ftl-quotes/hooks/use-trailer-sizes.tsx";
import { useTrailerTypes } from "~/features/ftl/ftl-quotes/hooks/use-trailer-types.tsx";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";

import {
	commodityValidator,
	customerPoValidator,
	loadValueValidator,
	SPECIAL_LOAD_INSTRUCTIONS_MAX_LENGTH,
	specialLoadInstructionsValidator,
	trailerSizeValidator,
	trailerTypeValidator,
	weightValidator,
} from "../../create-ftl-shipment-validators.ts";
import { useRequestedRate } from "../../hooks/use-requested-rate.tsx";
import styles from "./ftl-shipment-information-fields.module.css";

const temperatureValidator = (value: number | string | null | undefined) =>
	value == undefined || value === "" ? "Temperature is required." : "";

type FtlShipmentInformationFieldsProps = {
	selectedTrailerTypeIsRefrigerated: boolean;
	selectedTrailerTypeCode: string | undefined;
};

export function FtlShipmentInformationFields({
	selectedTrailerTypeIsRefrigerated,
	selectedTrailerTypeCode,
}: Readonly<FtlShipmentInformationFieldsProps>) {
	const [specialLoadInstructions, setSpecialLoadInstructions] = useState("");

	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const { requestedRateFeature } = useRequestedRate(selectedCustomer?.id ?? 0);
	const { trailerTypes, isLoading: isLoadingTrailerTypes } = useTrailerTypes();
	const { loadValues, isLoading: isLoadingLoadValues } = useLoadValues();
	const { trailerSizesData, isLoading: isLoadingTrailerSizes } =
		useTrailerSizes(selectedTrailerTypeCode ?? "");

	const handleSpecialLoadInstructionsChange = (event: TextAreaChangeEvent) => {
		setSpecialLoadInstructions(event.value ?? "");
	};

	return (
		<div className={styles.shipmentInfo}>
			<div
				className={styles.customerPo}
				data-testid={"customer-po"}
				id={"customer-po"}
			>
				<FormField
					required
					component={Input}
					label={"Customer PO"}
					name={"customerPo"}
					placeholder={"Enter Customer PO"}
					validator={customerPoValidator}
				/>
			</div>
			<div
				className={styles.trailerType}
				data-testid={"trailer-type"}
				id={"trailer-type"}
			>
				<FormField
					required
					component={DropDownList}
					data={trailerTypes ?? undefined}
					dataItemKey={"code"}
					label={"Trailer Type"}
					loading={isLoadingTrailerTypes}
					name={"trailerType"}
					textField={"displayName"}
					validator={trailerTypeValidator}
				/>
			</div>
			{selectedTrailerTypeIsRefrigerated ? (
				<div
					className={styles.trailerType}
					data-testid={"temperature"}
					id={"temperature"}
				>
					<FormField
						required
						component={NumericTextBox}
						format={"n0"}
						label={"Temperature (°F)"}
						max={99}
						min={-99}
						name={"temperature"}
						placeholder={"Enter temperature"}
						validator={temperatureValidator}
					/>
				</div>
			) : null}
			<div
				className={styles.trailerSize}
				data-testid={"trailer-size"}
				id={"trailer-size"}
			>
				<FormField
					required
					component={DropDownList}
					data={trailerSizesData ?? undefined}
					dataItemKey={"code"}
					label={"Trailer Size"}
					loading={isLoadingTrailerSizes}
					name={"trailerSize"}
					textField={"displayName"}
					validator={trailerSizeValidator}
				/>
			</div>
			<div
				className={styles.loadValue}
				data-testid={"load-value"}
				id={"load-value"}
			>
				<FormField
					required
					component={DropDownList}
					data={loadValues ?? undefined}
					dataItemKey={"code"}
					label={"Load Value"}
					loading={isLoadingLoadValues}
					name={"loadValue"}
					textField={"displayName"}
					validator={loadValueValidator}
				/>
			</div>
			<div
				className={styles.commodity}
				data-testid={"commodity"}
				id={"commodity"}
			>
				<FormField
					required
					component={Input}
					label={"Commodity description"}
					name={"commodity"}
					placeholder={"Enter commodity"}
					validator={commodityValidator}
				/>
			</div>
			<div
				className={styles.weight}
				data-testid={"total-weight"}
				id={"total-weight"}
			>
				<FormField
					required
					component={NumericTextBox}
					format={"n0"}
					label={"Total Weight (lbs.)"}
					max={999_999}
					min={1}
					name={"totalWeight"}
					validator={weightValidator}
				/>
			</div>
			<div
				className={styles.specialLoadInstructions}
				data-testid={"special-load-instructions"}
				id={"special-load-instructions"}
			>
				<FormField
					component={TextArea}
					label={"Special Load Instructions"}
					name={"specialLoadInstructions"}
					onChange={handleSpecialLoadInstructionsChange}
					resizable={"none"}
					rows={3}
					validator={specialLoadInstructionsValidator}
					value={specialLoadInstructions}
				/>
				<Hint direction={"end"}>
					{specialLoadInstructions.length}/
					{SPECIAL_LOAD_INSTRUCTIONS_MAX_LENGTH}
				</Hint>
			</div>
			{requestedRateFeature?.requestedRateEnabled ? (
				<div data-testid={"requested-rate"} id={"requested-rate"}>
					<FormField
						component={NumericTextBox}
						format={"c"}
						label={"Requested Rate"}
						max={999_999.99}
						min={0}
						name={"requestedRate"}
					/>
				</div>
			) : null}
			<div className={styles.hazmat} data-testid={"hazmat"} id={"hazmat"}>
				<FormField component={Checkbox} label={"Hazmat"} name={"hazmat"} />
			</div>
		</div>
	);
}
